import getDistance from 'geolib/es/getDistance';
import { Guide, Place, PlaceClassType, Trip } from './interfaces';
import { positionFromLatLng } from "./position";

export type Distance = {
    fromMe: number | undefined,
    fromTrip: number | undefined,
    fromLodging: number | undefined,
    fromGuide: number | undefined,
};
export function computeDistance(item: Place, trip: Trip | null, guide: Guide | null,
    deviceLocation: { lat: number, lng: number } | null, lodging: Place | null): Distance {
    const placePosition = positionFromLatLng(item);
    const tripPosition = positionFromLatLng(trip);
    const lodgingPosition = positionFromLatLng(lodging);
    const guidePosition = positionFromLatLng(guide);
    return {
        fromTrip: trip === null || !tripPosition || !placePosition
            ? undefined
            : getDistance(
                { latitude: placePosition.lat, longitude: placePosition.lng },
                { latitude: tripPosition.lat, longitude: tripPosition.lng }
        ),
        fromLodging: lodging === null || !lodgingPosition || !placePosition
            ? undefined
            : getDistance(
                { latitude: placePosition.lat, longitude: placePosition.lng },
                { latitude: lodgingPosition.lat, longitude: lodgingPosition.lng }
        ),
        fromMe: deviceLocation === null || !placePosition
            ? undefined : getDistance(
                { latitude: placePosition.lat, longitude: placePosition.lng },
                { latitude: deviceLocation.lat, longitude: deviceLocation.lng }
        ),
        fromGuide: guide === null || !guidePosition || !placePosition
            ? undefined
            : getDistance(
                { latitude: placePosition.lat, longitude: placePosition.lng },
                { latitude: guidePosition.lat, longitude: guidePosition.lng }
        ),
    };
}

export function distanceTextForCoords(
    item: Place,
    trip: Trip | null,
    guide: Guide | null,
    hasHotel: boolean,
    pType: PlaceClassType,
    coords: { lat: number, lng: number } | null,
    lodging: Place | null,
) {
    return distanceTextForDistance(trip, guide, hasHotel, pType, computeDistance(item, trip, guide, coords, lodging), lodging);
}

export function metersToMetricMessage(distanceInMeters: number) {
    if (distanceInMeters < 1000) {
        return `${Math.round(distanceInMeters * 1000) / 1000} meters` as string;
    }
    let distanceInKm;
    if (distanceInMeters < 10000) {
        distanceInKm = Math.round(distanceInMeters / 100) / 10;
    } else {
        distanceInKm = Math.round(distanceInMeters / 1000); // TODO round without using toStringAsPrecision()
    }
    return `${distanceInKm} km` as string;
}

export function distanceTextForDistance(
    trip: Trip | null,
    guide: Guide | null,
    hasHotel: boolean,
    pType: PlaceClassType,
    distance: Distance,
    lodging: Place | null,
) {
    if (pType === PlaceClassType.Activity) {
        // Prefer distance from the day's lodging, but fall back to distance from the trip
        // when the lodging can't be measured (e.g. a lodging that has no coordinates) before
        // giving up with 'somewhere'.
        if (hasHotel && distance.fromLodging !== undefined) {
            // A lodging's display name is its title; location is often empty for places.
            return `${metersToMetricMessage(distance.fromLodging)} from ${lodging?.title ?? lodging?.location ?? 'lodging'}`;
        }
        if (distance.fromTrip !== undefined) {
            return `${metersToMetricMessage(distance.fromTrip)} from ${trip?.location}`;
        }
        return 'somewhere';
    }
    if (guide && pType === PlaceClassType.Saved) {
        if (distance.fromGuide === undefined) {
            return 'somewhere';
        } else {
            return `${metersToMetricMessage(distance.fromGuide)} from ${guide?.location}`;
        }
    }

    if (!hasHotel && pType === PlaceClassType.Saved) {
        if (distance.fromMe !== undefined) {
            return `${metersToMetricMessage(distance.fromMe)} from here`;
        }
    }

    return '';
}
